import { ActionContext } from 'vuex';

import { MergedActivityProduct } from '@/activities/modals/createEditActivity/services/MergedActivityProductsService';
import {
  AmountType,
  findRelativeUnit,
  findTotalUnit,
  isRelativeUnit,
} from '@/activities/utils/amountsAndUnits/findUnit';
import { Unit } from '@/shared/api/rest/models';
import { RootState } from '@/store/types';

import { CreateEditActivityState } from '../types';
import initServices from './utils/initServices';

/**
 * Sets the activity-wide amount mode and converts every product to that mode (per ha <-> total,
 * keeping each product's base unit and value). This keeps all products of an activity consistent,
 * so their amounts are all fixed either as total or per ha.
 */
export default function setProductAmountModeForAllProducts(
  { state, rootState, rootGetters, getters, commit }: ActionContext<CreateEditActivityState, RootState>,
  mode: AmountType,
): void {
  commit('setProductAmountMode', mode);

  const { activityProductService } = initServices(state, rootState, rootGetters);
  const units = rootState.units.data;
  const sumProcessedArea = getters.sumProcessedArea as number;
  const mergedActivityProducts = getters.mergedActivityProducts as MergedActivityProduct[];

  mergedActivityProducts.forEach((activityProduct) => {
    const currentUnitId = activityProductService.findUnitIdWithFallback(activityProduct);
    if (!currentUnitId) return;
    const currentUnit = units[currentUnitId];
    if (!currentUnit) return;

    const targetUnit = findTargetUnit(currentUnit, mode, units);
    if (!targetUnit) return;

    const updatedActivityProduct = activityProductService.updateUnit(activityProduct, targetUnit.id, sumProcessedArea);
    const unit = updatedActivityProduct.unitId ? units[updatedActivityProduct.unitId] : null;
    if (!unit) return;

    commit('updateAmountAndUnitOnActivityProductInAllActivities', {
      activityProductId: activityProduct.id,
      updateValues: {
        amount: updatedActivityProduct.amount,
        unit,
      },
      rootState,
    });
  });
}

function findTargetUnit(currentUnit: Unit, mode: AmountType, units: Record<string, Unit>): Unit | null {
  if (mode === AmountType.PerHa) {
    return isRelativeUnit(currentUnit) ? currentUnit : findRelativeUnit(currentUnit, units);
  }
  return isRelativeUnit(currentUnit) ? findTotalUnit(currentUnit, units) : currentUnit;
}
